import { trackFunnelView } from '../../events/funnel-tracker';

type ConversionContext = 'filter-exclusive' | 'filter-gabarito' | 'gabarito-wrong' | 'gabarito-right' | 'banner-unlock';

interface ContextConfig {
  headline: string;
  sub: string;
  ctaText: string;
  highlightedFeature: 'gabarito' | 'exclusive';
}

const CONTEXT_MAP: Record<ConversionContext, ContextConfig> = {
  'filter-exclusive': {
    headline: 'Desbloqueie exercícios <span>exclusivos do TM+</span>',
    sub: 'Tenha acesso a conteúdos que não estão na versão gratuita e pratique sem limites.',
    ctaText: 'Desbloquear questões exclusivas →',
    highlightedFeature: 'exclusive',
  },
  'filter-gabarito': {
    headline: 'Entenda cada questão, <span>não só o gabarito.</span>',
    sub: 'O gabarito comentado explica o raciocínio por trás de cada resposta — para você aprender de verdade, não só decorar.',
    ctaText: 'Desbloquear gabarito comentado →',
    highlightedFeature: 'gabarito',
  },
  'gabarito-wrong': {
    headline: 'Entenda cada questão, <span>não só o gabarito.</span>',
    sub: 'O gabarito comentado explica o raciocínio por trás de cada resposta — para você aprender de verdade, não só decorar.',
    ctaText: 'Ver gabarito comentado completo →',
    highlightedFeature: 'gabarito',
  },
  'gabarito-right': {
    headline: 'Você acertou — confirme <span>o seu raciocínio.</span>',
    sub: 'O gabarito comentado confirma o seu raciocínio e mostra o que diferencia a resposta certa das outras alternativas — essencial para ir bem na prova.',
    ctaText: 'Ver explicação completa →',
    highlightedFeature: 'gabarito',
  },
  'banner-unlock': {
    headline: 'Desbloqueie exercícios <span>exclusivos do TM+</span>',
    sub: 'Tenha acesso a conteúdos que não estão na versão gratuita e pratique sem limites.',
    ctaText: 'Desbloquear questões exclusivas →',
    highlightedFeature: 'exclusive',
  },
};

const FUNNEL_ELEMENT_SUFFIX_MAP: Record<ConversionContext, string> = {
  'filter-exclusive': 'filter_exclusive',
  'filter-gabarito': 'filter_gabarito',
  'gabarito-wrong': 'gabarito_incorrect',
  'gabarito-right': 'gabarito_correct',
  'banner-unlock': 'banner_unlock',
};

/**
 * Funnel naming for the listing pages. The same markup serves /exercicios-enem/
 * and /questoes/, so the location/element prefix comes from the modal's
 * data-page-type. Shared with the inline gabarito CTA so both steps of the same
 * funnel report under the same location.
 */
export function getFunnelPageContext(): { location: string; elementPrefix: string } {
  const dialog = document.getElementById('tm-plus-conversion-modal') as HTMLDialogElement | null;
  const isAllYearsPage = dialog?.dataset.pageType === 'all-years';

  return {
    location: isAllYearsPage ? 'questoes' : 'exercises',
    elementPrefix: isAllYearsPage ? 'questoes' : 'enem',
  };
}

export function openConversionModal(context: ConversionContext) {
  const dialog = document.getElementById('tm-plus-conversion-modal') as HTMLDialogElement | null;
  if (!dialog) return;

  const config = CONTEXT_MAP[context];

  const headline = document.getElementById('conversion-modal-headline');
  const sub = document.getElementById('conversion-modal-sub');
  const cta = document.getElementById('conversion-modal-cta');
  const featureGabarito = document.getElementById('conversion-modal-feature-gabarito');
  const featureExclusive = document.getElementById('conversion-modal-feature-exclusive');

  if (headline) headline.innerHTML = config.headline;
  if (sub) sub.textContent = config.sub;
  if (cta) cta.textContent = config.ctaText;

  const { location, elementPrefix } = getFunnelPageContext();
  const funnelElement = `conversion_modal_${elementPrefix}_${FUNNEL_ELEMENT_SUFFIX_MAP[context]}`;

  // funnel_cta_click sai do funnel-tracker via estes atributos, como em
  // qualquer CTA da página (e é ele que persiste o last-touch para a
  // atribuição de compra).
  if (cta) {
    cta.setAttribute('data-funnel-click-location', location);
    cta.setAttribute('data-funnel-click-element', funnelElement);
    cta.setAttribute('data-funnel-click-type', 'gate');
  }

  // Toggle feature item highlights
  featureGabarito?.classList.toggle('tm-plus-conversion-modal__feature-item--highlighted', config.highlightedFeature === 'gabarito');
  featureExclusive?.classList.toggle('tm-plus-conversion-modal__feature-item--highlighted', config.highlightedFeature === 'exclusive');

  dialog.showModal();

  // O view não pode ficar ao cuidado do IntersectionObserver: o CTA é o mesmo
  // nó em todos os contextos e o observer só o vê uma vez, por isso a partir
  // da segunda variante da popup não saía view nenhum. O dedupe é partilhado,
  // logo não duplica quando o observer também apanha o primeiro contexto.
  trackFunnelView(location, funnelElement, 'gate');
}

export function setupConversionModal() {
  const dialog = document.getElementById('tm-plus-conversion-modal') as HTMLDialogElement | null;
  if (!dialog) return;

  // Close button
  const closeBtn = dialog.querySelector('.tm-plus-conversion-modal__close');
  closeBtn?.addEventListener('click', () => {
    dialog.close();
  });

  // Backdrop click to close
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) dialog.close();
  });

  // O CTA não precisa de listener próprio: o funnel-tracker apanha o click
  // pelos data-funnel-click-* postos em openConversionModal. Os antigos
  // paywall_popup_view/dismiss/cta_click e o checkout_start saíram — eram
  // redundantes com o par funnel_cta_view/click e ocupavam as custom
  // dimensions popup_variant e paywall_source.
}
